// src/stores/useAuthStore.ts
import { AuthUser } from "@/types/AuthUser";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { MetaStore } from "@/stores/metaStore";
import { useRoute, useRouter } from "vue-router";

import useAuthApi from "@/composables/useAuthApi";

export const useAuthStore = defineStore("auth", () => {
  const authUser = ref<AuthUser | null>(null);
  const isLoggedIn = computed(() => authUser.value !== null);
  const token = ref<string | null>(null);
  const loading = ref<boolean>(false);
  const error = ref<any>(false);
  const isAuthResolved = ref(false);

  const isSuperUser = computed<boolean>(() => !!authUser.value?.superUser);

  const authApi = useAuthApi();
  const metaStore = MetaStore();
  const router = useRouter();
  const route = useRoute();

  // Track in-flight auth request to prevent race conditions
  let authPromise: Promise<AuthUser | null> | null = null;

  async function getAuthUser(): Promise<AuthUser | null> {
    // If already resolved, return cached user
    if (isAuthResolved.value) {
      return authUser.value;
    }

    // If auth is in progress, return the existing promise
    if (authPromise) {
      return authPromise;
    }

    // Start new auth request
    authPromise = (async () => {
      try {
        const token = window.location.hash.slice(1);

        if (token.length > 50) {
          await loginWithToken(token);
          router.replace({ path: route.path, query: route.query });
          isAuthResolved.value = true;
          return null;
        }

        const result = await authApi.get("api/auth/UserInfo");
        authUser.value = result.data as AuthUser;
        return result.data as AuthUser;
      } catch (error: any) {
        if (error?.response?.status === 401) {
          logOut();
        }
        throw error;
      } finally {
        isAuthResolved.value = true;
        authPromise = null; // Clear the promise
      }
    })();

    return authPromise;
  }

  async function loginWithToken(token: string) {
    try {
      loading.value = true;
      await authApi.post("api/token", { Token: token });
      metaStore.meta = true;
      router.push("/");
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  }

  function logoutTimer(expirationTime: number) {
    setTimeout(() => {
      logOut();
    }, expirationTime * 1000);
  }

  async function loginLibra(): Promise<string> {
    const res = await authApi.get("api/auth/five/token");
    return res.data as string;
  }

  async function logOut() {
    await authApi.post("api/logout");
    authUser.value = null;

    localStorage.removeItem("meta");
    localStorage.removeItem("lifeyrisgatt");
    localStorage.removeItem("notAccepted");

    const pathname = window.location.pathname || "";

    let redirectPath = "";

    // If redirect is to logout page, keep it empty
    if (pathname !== "/logout") {
      redirectPath = pathname.replaceAll("/", "$").substring(1);
    }


    let id = "";
    if (window.location.href.includes("localhost")) {
      id = "localhost.gildi.is";
    } else if (window.location.href.includes("gildi.demo01.lisa.is")) {
      id = "gildi.demo01.lisa.is";
    } else if (window.location.href.includes("sjodfelagavefur.gildi.is")) {
      id = "sjodfelagavefur.gildi.is";
    }
    if (id) {
      if(id.includes("sjodfelagavefur")){
        window.location.href = `https://login.signet.is/login/?id=${id}&path=${redirectPath}`;
      }
      else{
        window.location.href = `https://prufa.signet.is/login/Login?id=${id}&path=${redirectPath}`;
      }
    }
  }

  return {
    authUser: computed(() => (authUser.value || ({} as AuthUser))),
    token,
    loading,
    error,
    isLoggedIn,
    isAuthResolved,
    isSuperUser, // NEW export

    loginLibra,
    getAuthUser,
    loginWithToken,
    logoutTimer,
    logOut,
  };
});
